export const MAX_ACTIVE_ROW_REQUESTS = 4;
export default class PageRowScheduler {
    options;
    entries = new Map();
    /**
     * Configuration order is only used as a tie-breaker when two rows have
     * the same priority.
     */
    order = [];
    activeCount = 0;
    constructor(options) {
        this.options = options;
    }
    /**
     * Updates the configuration order used by the scheduler.
     *
     * The scheduler never changes this order based on request completion.
     * It is only used as a deterministic tie-breaker for equal priorities.
     */
    setOrder(keys) {
        this.order = keys;
    }
    /**
     * Reports the latest viewport eligibility and priority for a row.
     *
     * - Eligible idle rows enter the queue.
     * - Queued rows that leave the eligibility window return to idle.
     * - Active rows are never cancelled by ordinary scrolling.
     * - Completed rows are never automatically reloaded.
     */
    report(key, { isEligible, priority, }) {
        const entry = this.entries.get(key) ?? this.create(key);
        entry.isEligible = isEligible;
        entry.priority = priority;
        // Active requests are intentionally unaffected by scrolling.
        if (entry.status === "active") {
            return;
        }
        // Resolved rows do not reload just because they receive another
        // eligibility report.
        if (entry.status === "done") {
            return;
        }
        if (isEligible) {
            if (entry.status === "idle") {
                entry.status = "queued";
                this.options.onStatusChange(key, "queued");
            }
        }
        else if (entry.status === "queued") {
            entry.status = "idle";
            entry.retryPriority = false;
            this.options.onStatusChange(key, "idle");
        }
        this.pump();
    }
    /**
     * Manually retries a failed row.
     *
     * The retry:
     * - respects the global concurrency cap;
     * - only queues an eligible row;
     * - gets priority over normal queued rows;
     * - does not cancel active requests.
     */
    retry(key) {
        const entry = this.entries.get(key) ?? this.create(key);
        if (entry.status === "active" || entry.status === "queued") {
            return;
        }
        if (!entry.isEligible) {
            return;
        }
        entry.status = "queued";
        entry.retryPriority = true;
        this.options.onStatusChange(key, "queued");
        this.pump();
    }
    /**
     * Invalidates one row because its configuration changed or it was removed.
     *
     * Any active request is deliberately cancelled.
     * The entry is removed completely so a subsequent report creates a fresh
     * scheduler entry.
     */
    invalidate(key) {
        const entry = this.entries.get(key);
        if (!entry) {
            return;
        }
        entry.controller?.abort();
        this.entries.delete(key);
    }
    /**
     * Cancels all queued and active work.
     *
     * The scheduler remains reusable after reset.
     *
     * React row state is intentionally not changed here. The owning controller
     * is responsible for resetting its row view models when the request context
     * changes.
     */
    reset() {
        for (const entry of this.entries.values()) {
            entry.controller?.abort();
        }
        this.entries.clear();
    }
    create(key) {
        const entry = {
            status: "idle",
            priority: Number.POSITIVE_INFINITY,
            isEligible: false,
            controller: null,
            retryPriority: false,
        };
        this.entries.set(key, entry);
        return entry;
    }
    /**
     * Starts queued work until the concurrency limit is reached.
     */
    pump() {
        const maxActive = this.options.maxActive ?? MAX_ACTIVE_ROW_REQUESTS;
        while (this.activeCount < maxActive) {
            const next = this.pickNext();
            if (!next) {
                return;
            }
            this.start(next[0], next[1]);
        }
    }
    /**
     * Finds the highest-priority queued row.
     *
     * Ordering:
     * 1. Manual retry rows.
     * 2. Viewport priority (lower number = closer).
     * 3. Configuration order.
     */
    pickNext() {
        let best = null;
        for (const entryPair of this.entries) {
            const [key, entry] = entryPair;
            if (entry.status !== "queued") {
                continue;
            }
            if (!entry.isEligible) {
                continue;
            }
            if (!best || this.isBefore(entryPair, best)) {
                best = [key, entry];
            }
        }
        return best;
    }
    isBefore(entryPairA, entryPairB) {
        const [, entryA] = entryPairA;
        const [, entryB] = entryPairB;
        // Manual retry gets precedence over normal viewport priority.
        if (entryA.retryPriority !== entryB.retryPriority) {
            return entryA.retryPriority;
        }
        // Lower priority number means closer to the viewport.
        if (entryA.priority !== entryB.priority) {
            return entryA.priority < entryB.priority;
        }
        // Configuration order is the deterministic tie-breaker.
        return this.getOrderIndex(entryPairA[0]) < this.getOrderIndex(entryPairB[0]);
    }
    getOrderIndex(key) {
        const index = this.order.indexOf(key);
        // Unknown rows should sort after configured rows.
        return index === -1 ? Number.POSITIVE_INFINITY : index;
    }
    /**
     * Starts one request and owns its lifecycle.
     */
    start(key, entry) {
        const controller = new AbortController();
        entry.status = "active";
        entry.controller = controller;
        entry.retryPriority = false;
        this.activeCount += 1;
        this.options.onStatusChange(key, "active");
        void this.options
            .run(key, controller.signal)
            .catch(() => undefined)
            .finally(() => {
            this.activeCount -= 1;
            /**
             * Only mutate the entry if this is still the exact request that
             * owns it.
             *
             * This is important when invalidate/reset happens while an async
             * request is still settling.
             */
            const currentEntry = this.entries.get(key);
            if (currentEntry === entry && entry.controller === controller) {
                entry.controller = null;
                if (controller.signal.aborted) {
                    entry.status = "idle";
                }
                else {
                    entry.status = "done";
                }
            }
            /**
             * A slot has now become available, so immediately start the
             * highest-priority queued eligible row.
             */
            this.pump();
        });
    }
}
